import { isEmpty } from 'lodash';

import WidgetModel, {
  ValidationMessage,
  WidgetTypes,
} from '@mm/state/model/WidgetModel';
import { validationMessages } from '../../../helpers/validationMessages';
import { filterOutWidgetErrorsBasedOnKind } from '../../../../../../common/helpers/widget-helpers/use-email-widget-handlers';

export type WidgetErrors = {
  entries: string[];
  widget: string | null;
};

// rename parameter because 'arguments' is reserved
function getErrorMessage(
  validationMessage: ValidationMessage,
  model: WidgetModel,
): string {
  if (!Array.isArray(validationMessage?.rule?.arguments)) {
    if (!Object.keys(validationMessage).find(key => key === 'rule')) {
      return validationMessage.toString();
    }
    const fieldName = validationMessage?.field?.split('/').pop() as string;
    return validationMessages(
      {
        fieldName,
        errorType: validationMessage?.rule?.type,
        value:
          validationMessage?.rule?.arguments || validationMessage?.rule?.value,
      },
      model,
    );
  }

  return validationMessage?.rule?.arguments
    .map(arg => getErrorMessage(arg, model))
    .filter(val => val !== '')
    .filter((val, index, self) => self.indexOf(val) === index)
    .reduce((acc, errorMessage) => `${acc}${errorMessage}\n`, '')
    .slice(0, -1);
}

export const getWidgetErrors = (
  widget: WidgetModel,
  isValid: boolean,
): WidgetErrors => {
  const errors: WidgetErrors = {
    entries: [],
    widget: null,
  };

  if (!isValid) {
    const { validationMessages } = widget;
    let errorMessages: ValidationMessage[] = [];

    // The body of the widget is the most important peace in the UI. If the body is missing
    // then we start by only showing body validation messages. This helps particularly when
    // parsing error messages from a json-schema validation result. For example, if the body of
    // question is empty, want to tell the user right away that "Question cannot be empty".

    if (validationMessages) {
      const filteredMessages = validationMessages.filter(
        m => m.field === '#/config/body',
      );

      let filteredValidationMessages = validationMessages;
      // Note: need to filter error messages based on notification kind
      if (widget.type === WidgetTypes.EMAIL) {
        filteredValidationMessages = validationMessages.map(m => ({
          ...m,
          rule: {
            ...m.rule,
            arguments: filterOutWidgetErrorsBasedOnKind(widget.config.kind, m),
          },
        }));
      }

      // This is confusing
      if (filteredMessages.length > 0) {
        errorMessages = filteredMessages;
      } else {
        errorMessages = filteredValidationMessages;
      }

      errorMessages
        .filter(message => !isEmpty(message))
        .forEach(message => {
          const entriesTestResult = /\/entries\/(\d\d|\d)/.exec(message.field);
          if (entriesTestResult) {
            const entryIndex = entriesTestResult[1];
            const entryError = errors.entries[Number(entryIndex)];
            // Workaround with repeating errors on same entry, not to override existing errors
            errors.entries[Number(entryIndex)] =
              entryError || getErrorMessage(message, widget);
          } else {
            errors.widget = getErrorMessage(message, widget);
          }
        });
    }
  }

  return errors;
};
